"use client";

import { CheckIcon as CheckMark } from "lucide-react";
import { cn } from "@/lib/utils";
import { FeatureList } from "./FeatureList";
import { formatHKD, type AccountingPlan } from "./pricing";

/**
 * A selectable accounting tier: the priced card plus its feature list beneath.
 *
 * `framed` is the review page's treatment, which wraps the pair in its own
 * panel and outlines the card; the accounting step uses the bare version.
 */
export function AccountingPlanColumn({
  plan,
  selected,
  onSelect,
  framed = false,
  tickClassName,
}: {
  plan: AccountingPlan;
  selected: boolean;
  onSelect: (id: string) => void;
  framed?: boolean;
  tickClassName?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-1 flex-col items-start gap-6",
        framed &&
          "overflow-hidden rounded-3xl bg-[#0E1116] pb-6 outline outline-1 -outline-offset-1 outline-[#30363D]",
      )}
    >
      <button
        type="button"
        onClick={() => onSelect(plan.id)}
        aria-pressed={selected}
        className={cn(
          "relative flex min-h-[188px] w-full flex-col items-start gap-4 overflow-hidden rounded-[28px] bg-[#0F1319] p-6 text-left outline outline-2 -outline-offset-2 transition-colors sm:rounded-[32px] sm:p-8",
          framed && "sm:p-6",
          selected
            ? "outline-[#58A6FF]"
            : framed
              ? "outline-white/60 hover:outline-white"
              : "outline-transparent hover:outline-white/20",
        )}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-20 -left-10 h-[171px] w-[171px] rounded-full bg-[#70A6FF] blur-[72px]"
        />
        <div className="relative flex w-full flex-col items-start gap-1">
          <span className="font-inter text-xl font-bold text-white sm:text-[22px]">{plan.name}</span>
          <span className="font-inter text-sm leading-[19.6px] text-[#9C9C9C]">
            {plan.description}
          </span>
        </div>
        <div className="relative">
          <span className="font-inter text-xl font-extrabold text-white sm:text-[23.36px]">
            {formatHKD(plan.price)}
          </span>
          <span className="font-inter text-[13.58px] font-normal text-white">{plan.period}</span>
        </div>

        {/* Below lg the tiers are a carousel showing one card at a time, so the
            card has to state its own selection — there's no unselected sibling
            to compare its ring against. A span rather than a button: the card
            is already the button, and nesting one inside another is invalid
            HTML. Clicks land on the card either way. */}
        <span
          className={cn(
            "relative mt-auto flex h-11 w-full items-center justify-center gap-2 rounded-full font-inter text-sm font-bold transition-colors lg:hidden",
            selected ? "bg-[#58A6FF] text-[#08111F]" : "bg-white/10 text-white",
          )}
        >
          {selected ? (
            <>
              <CheckMark aria-hidden className="h-4 w-4 shrink-0" />
              Selected
            </>
          ) : (
            "Select this plan"
          )}
        </span>
      </button>

      <FeatureList
        title={plan.featuresTitle}
        items={plan.features}
        // The framed variant has no padding of its own, so the list would
        // otherwise sit flush against the panel's visible border.
        className={cn("w-full", framed && "px-6")}
        tickClassName={tickClassName}
      />
    </div>
  );
}
